import {Injectable} from '@angular/core';
import {ActivatedRoute, ActivationEnd, NavigationEnd, NavigationStart, Params, Router} from '@angular/router';
import {Outlets} from '@entities/common/common.enums';
import {Page, SubPage, Vendor} from '@entities/left-panel/left-panel.interface';
import {ChangeUrlParams, GoParams} from '@entities/router/router.interfaces';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {Store} from '@ngrx/store';
import {AppState} from '@store';
import {LeftPanelActions} from '@store/left-panel/actions';
import {leftPanelSelector} from '@store/left-panel/selectors';
import {routerActions} from '@store/route/actions';
import {clone, equals, flatten, mergeDeepRight} from 'ramda';
import {combineLatest} from 'rxjs';
import {filter, take, tap, withLatestFrom} from 'rxjs/operators';

/**
 * This class is an injectable Angular service that provides a way to fetch data for  Page.
 */
@Injectable()
export class RouterEffects {
	/**
	 * Auxiliary variable for accessing to page_route_size.
	 */
	private readonly PAGE_ROUTE_SIZE = 1;
	/**
	 * Auxiliary variable for accessing to sub_page_route_params_size.
	 */
	private readonly SUB_PAGE_ROUTE_PARAMS_SIZE = 3;
	/**
	 * Auxiliary variable for accessing to vendor_route_size.
	 */
	private readonly VENDOR_ROUTE_SIZE = 5;

	/**
	 * Auxiliary variable for accessing to destination url.
	 */
	private destinationUrl: string;

	/**
	 * This function creates an Angular effect that go.
	 * @returns an Observable that emits data when the effect completes
	 */
	go$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(routerActions.go),
				tap(({payload}) => {
					this.router.navigate(
						Array.isArray(payload.url) ? payload.url : [payload.url],
						payload.extras || {}
					);
				})
			),
		{
			dispatch: false,
		}
	);

	/**
	 * This function creates an Angular effect that set param.
	 * @returns an Observable that emits data when the effect completes
	 */
	setParam$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(routerActions.setQueryParams),
				tap(({payload}) => {
					this.router.navigate([], {
						relativeTo: this.route,
						queryParams: payload.params,
						queryParamsHandling: 'merge',
					});
				})
			),
		{
			dispatch: false,
		}
	);

	/**
	 * Constructor for RouterEffects class.
	 * @constructor
	 * @param {Actions} actions$ actions
	 * @param {Router} router router
	 * @param {ActivatedRoute} route route
	 * @param {Store<AppState>} store store
	 */
	constructor(
		private actions$: Actions,
		private router: Router,
		private route: ActivatedRoute,
		private store: Store<AppState>
	) {
		this.listenToRouter();
	}

	/**
	 * Auxiliary method (function) for accessing to listen to router.
	 * @returns {void}
	 */
	private listenToRouter() {
		this.router.events
			.pipe(filter((event) => event instanceof NavigationStart))
			.subscribe((event: NavigationStart) => {
				if (event.url !== this.destinationUrl) {
					this.destinationUrl = event.url;
					const payload: ChangeUrlParams = {url: this.destinationUrl};
					this.store.dispatch(routerActions.changeUrl({payload}));
				}
			});
		this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
			if (this.destinationUrl !== event.urlAfterRedirects) {
				this.destinationUrl = event.urlAfterRedirects;
				const payload: ChangeUrlParams = {url: this.destinationUrl};
				this.store.dispatch(routerActions.changeUrl({payload}));
			}
		});
		combineLatest(
			this.store.select(leftPanelSelector.pages).pipe(
				filter((pages) => !!pages?.length),
				take(1)
			),
			this.router.events.pipe(
				filter(
					(event) =>
						event instanceof ActivationEnd &&
						event.snapshot &&
						event.snapshot.outlet === Outlets.Primary &&
						!event.snapshot.firstChild
				)
			)
		)
			.pipe(
				withLatestFrom(
					this.store.select(leftPanelSelector.activePage),
					this.store.select(leftPanelSelector.activeVendor)
				)
			)
			.subscribe(([[pages, event], activePage, activeVendor]: [[Page[], ActivationEnd], Page, Vendor]) => {
				let route = event.snapshot;
				let params = clone(route.params);

				while (route.firstChild) {
					route = route.firstChild;
					if (route.params) {
						params = mergeDeepRight(params, route.params);
					}
				}

				const path: string[] = flatten<any>(
					route.pathFromRoot
						.filter((r) => r.routeConfig && r.routeConfig.path)
						.map((r) => r.routeConfig.path.split('/'))
				);

				const [urlWithoutQueryString, queryString] = this.destinationUrl.split('?');

				if (
					`/${this.getPathWithParams(path, params).join('/')}` !== urlWithoutQueryString &&
					urlWithoutQueryString !== '/'
				) {
					return;
				}

				const pageUpdateAction = this.getPageUpdateAction(pages, path, params, activePage, activeVendor);

				if (pageUpdateAction) {
					this.store.dispatch(pageUpdateAction);
				}

				let activeRoute = this.route.snapshot.children.find((r) => r.outlet === Outlets.Filters);
				if (!activeRoute) {
					const payload: GoParams = {
						url: {
							outlets: {
								primary: this.getPathWithParams(path, params),
								filters: this.getPathWithParams(path, params),
							},
						},
						extras: {queryParams: this.parseQueryString(queryString)},
					};
					this.store.dispatch(routerActions.go({payload}));
					return;
				}
				let activeParams = clone(activeRoute.params);
				while (activeRoute.firstChild) {
					activeRoute = activeRoute.firstChild;
					if (activeRoute.params) {
						activeParams = mergeDeepRight(params, activeRoute.params);
					}
				}

				const activePath = flatten<any>(
					activeRoute.pathFromRoot
						.filter((r) => r.routeConfig && r.routeConfig.path)
						.map((r) => r.routeConfig.path.split('/'))
				);

				if (!equals(this.getPathWithParams(path, params), this.getPathWithParams(activePath, activeParams))) {
					const payload: GoParams = {
						url: {
							outlets: {
								primary: this.getPathWithParams(path, params),
								filters: this.getPathWithParams(path, params),
							},
						},
						extras: {queryParams: this.parseQueryString(queryString)},
					};
					this.store.dispatch(routerActions.go({payload}));
				}
			});
	}

	/**
	 * Auxiliary function for accessing to get page update action.
	 * @param {Page[]} pages
	 * @param {string[]} path
	 * @param {Params} params
	 * @param {Page} activePage
	 * @param {SubPage} activeSubPage
	 * @param {Vendor} activeVendor
	 * @returns {any}
	 */
	private getPageUpdateAction(
		pages: Page[],
		path: string[],
		params: Params,
		activePage: Page,
		activeVendor: Vendor
	): any {
		const selectedPage: Page = pages.find((page) => page.url === path[0]);
		let selectedSubPage: SubPage = null;
		let selectedVendor: Vendor = null;

		if (selectedPage && path.length > this.PAGE_ROUTE_SIZE) {
			const subPageBase = `${path[this.SUB_PAGE_ROUTE_PARAMS_SIZE - 2]}`;
			const subPagePath =
				path.length >= this.SUB_PAGE_ROUTE_PARAMS_SIZE
					? `${subPageBase}/${String(params[path[this.SUB_PAGE_ROUTE_PARAMS_SIZE - 1].substring(1)])}`
					: subPageBase;
			selectedSubPage = selectedPage.subPages.find((subPage) => subPage.url === subPagePath);

			if (selectedSubPage && path.length > this.SUB_PAGE_ROUTE_PARAMS_SIZE) {
				const vendorPath = `${path[this.VENDOR_ROUTE_SIZE - 2]}/${String(
					params[path[this.VENDOR_ROUTE_SIZE - 1].substring(1)]
				)}`;
				selectedVendor = selectedSubPage.vendors.find((vendor) => vendor.url === vendorPath);
			}
		}

		if (!selectedPage || (equals(selectedPage, activePage) && equals(selectedVendor, activeVendor))) {
			return null;
		}

		return LeftPanelActions.selection({
			page: selectedPage,
			subPage: selectedSubPage,
			vendor: selectedVendor,
		});
	}

	/**
	 * Auxiliary function for accessing to get path with params.
	 * @param {string[]} path
	 * @param {Record<string, string>} params
	 * @returns {string[]}
	 */
	private getPathWithParams(path: string[], params: Record<string, string>): string[] {
		return path.map((field) => (field[0] === ':' ? params[field.slice(1)] : field));
	}

	/**
	 * Auxiliary function for accessing to parse query string.
	 * @param {string} queryString
	 * @returns {Record<string, string>}
	 */
	private parseQueryString(queryString: string): Record<string, string> {
		if (!queryString) {
			return null;
		}
		return JSON.parse(
			'{"' + decodeURI(queryString).replace(/"/g, '\\"').replace(/&/g, '","').replace(/=/g, '":"') + '"}'
		);
	}
}
